import { motion } from 'framer-motion';

/**
 * A–Z index as a vertical rail pinned to the employee table wrapper.
 *
 * It used to be a full-width horizontal strip which, together with the
 * anniversaries banner, pushed the first employee row to roughly y=570 — about
 * 180px of chrome before any content. Vertical costs no vertical space at all.
 * Sits in the gutter between the sidebar and the table (not off on the far
 * right past the favourite column, where it read as disconnected from the
 * name column it indexes).
 *
 * Hidden below lg: 26 targets cannot meet the 44px touch-target rule on a phone,
 * and search plus the department rail already cover quick filtering there. The
 * list container carries matching left padding so the rail sits in its own
 * gutter rather than over the name column or the scrollbar.
 */
export function AlphabetNav({ onLetterClick, availableLetters = [], selectedLetter = null }) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  return (
    <div
      className="hidden lg:flex flex-col items-center gap-px absolute left-1 top-0 z-[2]
                 py-2 px-0.5 rounded-full bg-surface/80 border border-line shadow-e1 backdrop-blur-[6px]"
      aria-label="Jump to letter"
    >
      {alphabet.map((letter) => {
        const isAvailable = availableLetters.includes(letter);
        const isSelected = letter === selectedLetter;

        if (!isAvailable) {
          return (
            <span
              key={letter}
              aria-hidden="true"
              className="w-5 h-[17px] text-[9.5px] font-semibold rounded flex items-center justify-center select-none text-ink-4/45"
            >
              {letter}
            </span>
          );
        }

        return (
          <motion.button
            key={letter}
            onClick={() => onLetterClick(letter)}
            whileTap={{ scale: 0.85 }}
            aria-pressed={isSelected}
            className={`w-5 h-[17px] text-[9.5px] font-semibold rounded flex items-center justify-center
              transition-colors cursor-pointer
              ${isSelected
                ? 'bg-accent-soft text-accent-ink ring-1 ring-accent-line'
                : 'text-accent hover:bg-accent-soft'}`}
            title={isSelected ? `Clear ${letter} filter` : `Filter by ${letter}`}
          >
            {letter}
          </motion.button>
        );
      })}
    </div>
  );
}
